import { useEffect, useRef } from 'react';

/**
 * Mounts a third-party embed supplied as a raw HTML snippet.
 *
 * Pasting an embed straight into JSX does not work: JSX treats it as text, and
 * markup set through innerHTML has its <script> tags flagged as already-run, so
 * the provider's loader never fires. This parses the snippet and rebuilds every
 * script as a fresh element, which does execute once appended.
 *
 * Guarded against StrictMode's double effect so the loader only runs once.
 */
export default function HtmlEmbed({ html, className }) {
  const ref = useRef(null);

  useEffect(() => {
    const host = ref.current;
    if (!host || !html || host.dataset.mounted === 'true') return;
    host.dataset.mounted = 'true';

    const template = document.createElement('template');
    template.innerHTML = html.trim();

    for (const original of template.content.querySelectorAll('script')) {
      const script = document.createElement('script');
      for (const { name, value } of original.attributes) script.setAttribute(name, value);
      script.textContent = original.textContent;
      original.replaceWith(script);
    }

    host.appendChild(template.content);
  }, [html]);

  return <div className={className} ref={ref} />;
}
